import React from 'react'

export function useAudioPreview() {
  const audioRef = React.useRef<HTMLAudioElement | null>(null)
  const [playingId, setPlayingId] = React.useState<string | null>(null)

  const stop = React.useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      // Optionally clear src to release resource
      audio.src = ''
    }
    setPlayingId(null)
  }, [])

  const toggle = React.useCallback(async (id: string, url?: string | null) => {
    // If no URL is provided, stop current playback if this id is playing
    if (!url || !url.trim()) {
      if (playingId === id) {
        stop()
      }
      return
    }
    
    let audio = audioRef.current
    if (!audio) {
      audio = new Audio()
      audioRef.current = audio
      audio.addEventListener('ended', () => setPlayingId(null))
    }
    
    // If this id is currently playing, pause it
    if (playingId === id && audio && !audio.paused) {
      audio.pause()
      setPlayingId(null)
      return
    }
    
    // Stop any other audio that might be playing
    if (audio && !audio.paused) audio.pause()
    
    // Start playing the new audio
    if (audio) {
      audio.src = url
      try {
        await audio.play()
        setPlayingId(id)
      } catch {
        // Audio failed to play, don't set playing state
        setPlayingId(null)
      }
    }
  }, [playingId, stop])

  React.useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
    }
  }, [])

  return { playingId, toggle, stop }
}
